import { fieldTypeOf } from '../field-types';
import type { FieldTypeCard, Quantity } from '../field-types/card';
import { findField } from '../format/elements';
import type { FieldElement, FormDefinition } from '../format/form-definition';
import { isReference, type Bound, type RangeValidation } from '../format/pieces';
import { elementName } from '../texts/words';

import { isFilled } from './required';
import { SaidBound, USER_PLACEHOLDERS } from '../texts/userPlaceholders';
import { FillBound } from '../field-types/fillInput';
import { builtInCheckError } from './build-in-checks';
import { DATE_FORMAT, formatDate } from 'framework/utils/hooks/useDateService';

export type ResolvedBound = FillBound & SaidBound;

export type ResolvedRangeValidation = Omit<RangeValidation, 'when' | 'min' | 'max'> & {
  min?: ResolvedBound;
  max?: ResolvedBound;
};

export const rangeError = (
  card: FieldTypeCard,
  value: NonNullable<unknown>,
  { min, max }: ResolvedRangeValidation,
): string | undefined => {
  const quantity = card.quantity?.(value);
  if (quantity === undefined) return undefined;
  const words = USER_PLACEHOLDERS.range.byKind[card.valueKind];
  if (min && isBelow(quantity, min.value)) return words.min(min);
  if (max && isBelow(max.value, quantity)) return words.max(max);
  return undefined;
};

const isBelow = (a: Quantity, b: Quantity): boolean => {
  if (typeof a === 'number' && typeof b === 'number') return a < b;
  if (typeof a === 'string' && typeof b === 'string') return a < b;
  return false;
};

export const valueAsBound = (field: FieldElement, value: unknown): Quantity | undefined => {
  const card = fieldTypeOf(field);
  if (!isFilled(card, value) || builtInCheckError(field, value) !== undefined) return undefined;
  return card.quantity?.(value);
};

export const boundWords = (definition: FormDefinition, bound: Bound): string => {
  if (!isReference(bound)) {
    return typeof bound === 'number' ? String(bound) : (formatDate(bound, DATE_FORMAT.FULL_DATE)?.trim() ?? bound);
  }
  if (bound.kind === 'today') return USER_PLACEHOLDERS.range.today;
  const field = findField(definition, bound.fieldId);
  return field ? elementName(field) : USER_PLACEHOLDERS.range.deletedField;
};

export const sayBound = (definition: FormDefinition, bound: Bound): SaidBound => {
  return { said: boundWords(definition, bound), one: bound === 1 };
};
